import React, { Component } from 'react';
import PropTypes from 'prop-types';
import PropTypesImmutable from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Row, Col, Spin, Button, Tooltip, Alert } from 'antd';

import { Form, Field } from 'react-final-form';

import { NumberField } from '../../atoms';
import { RegisterActions } from '../../components';

import validate from './validateSchema';

import {
  FORM_MEASUREMENT_DATA,
  measurementDataFormFields,
  dataStatus,
  issuedGosStatus,
} from '../../constants/formsAddEquipment';

import {
  addHydrogenMeasurementData,
  fetchHydrogenMeasurementDataForProductionPeriod,
  confirmHydrogenMeasurementData,
  fetchPreviousHydrogenMeasurementSurplus,
  resetPreviousHydrogenMeasurementSurplus,
  fetchHydrogenGosIssued,
  resetHydrogenGosIssued,
} from '../../actions/actionCreators/equipmentAddHydrogenMeasurementDataActionCreators';

import {
  getHydrogenMeasurementData,
  getLoadingMeasurementData,
  getPreviousHydrogenMeasurementSurplus,
  getIsGosIssued,
} from '../../reducers/equipmentAddMeasurementDataReducer';

import {
  fetchHydrogenFuelDataForProductionPeriod,
  issueHydrogenGos,
} from '../../actions/actionCreators/productionUnitAddActionCreators';
import { getHydrogenFuelData } from '../../reducers/productionUnitHydrogenFuelReducer';
import dayjs from '../../utils/dayjs';
import { round } from '../../utils/numberHelper';
import { getLocale } from '../../reducers/settingsReducer';
import { makeRffValidateFromLegacy } from '../rffLegacyValidateAdapter';
import FormStateToRedux from '../FormStateToRedux';
import { isEqual } from 'lodash/fp';

export class AddEquipmentMeasurementData extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    hasPrevious: PropTypes.bool,
    hasDone: PropTypes.bool,
    goPrevious: PropTypes.func,
    productionUnit: PropTypesImmutable.map,
    inEditForNotAdmin: PropTypes.bool.isRequired,
    isEditMode: PropTypes.bool.isRequired,
    isAdmin: PropTypes.bool.isRequired,
    showRequestChangeModal: PropTypes.func,
    isHydrogenContext: PropTypes.bool,
    onSubmitSuccess: PropTypes.func,

    // data
    loading: PropTypes.bool,
    initialValues: PropTypesImmutable.map,
    measurementData: PropTypesImmutable.map,
    surplus: PropTypes.number,
    locale: PropTypes.string,
    proportions: PropTypes.object,
    hydrogenFuelData: PropTypesImmutable.map,
    gosStatus: PropTypes.string,

    // actions (bound)
    addHydrogenMeasurementData: PropTypes.func.isRequired,
    confirmHydrogenMeasurementData: PropTypes.func.isRequired,
    fetchHydrogenMeasurementDataForProductionPeriod: PropTypes.func.isRequired,
    fetchPreviousHydrogenMeasurementSurplus: PropTypes.func.isRequired,
    resetSurplus: PropTypes.func.isRequired,
    fetchHydrogenFuelDataForProductionPeriod: PropTypes.func.isRequired,
    issueHydrogenGos: PropTypes.func.isRequired,
    isGosIssued: PropTypes.func.isRequired,
    resetGosIssued: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.validate = makeRffValidateFromLegacy(
      validate,
      props,
      props.initialValues
    );
  }

  getStatus = () => {
    const { measurementData } = this.props;
    if (measurementData) return measurementData.get('status');
    return undefined;
  };

  componentDidMount() {
    const {
      productionUnit,
      fetchHydrogenMeasurementDataForProductionPeriod,
      fetchPreviousHydrogenMeasurementSurplus,
      resetSurplus,
      fetchHydrogenFuelDataForProductionPeriod,
      isGosIssued,
      resetGosIssued,
    } = this.props;

    resetSurplus();
    resetGosIssued();

    const productionUnitId = productionUnit.get('id');

    const prev = dayjs().subtract(1, 'month');
    const month = prev.month() + 1;
    const year = prev.year();

    fetchHydrogenMeasurementDataForProductionPeriod({
      productionUnitId,
      month,
      year,
    });
    fetchHydrogenFuelDataForProductionPeriod({ productionUnitId, year, month });
    fetchPreviousHydrogenMeasurementSurplus({ productionUnitId, month, year });
    isGosIssued({ productionUnitId, month, year });
  }

  onSubmit = async (formValues) => {
    const {
      measurementData,
      hydrogenFuelData,
      isAdmin,
      gosStatus,
      productionUnit,
      addHydrogenMeasurementData,
      confirmHydrogenMeasurementData,
      issueHydrogenGos,
      isGosIssued,
      onSubmitSuccess,
    } = this.props;

    const data = measurementData?.toJS();
    const postData = {
      ...formValues,
      status: data.status,
      year: data.year,
      month: data.month,
      productionUnit: data.productionUnit,
    };

    if (data.status === dataStatus.OPEN) {
      await addHydrogenMeasurementData(postData);
    } else if (
      (data.status === dataStatus.PENDING ||
        (data.status === dataStatus.CONFIRMED &&
          gosStatus === issuedGosStatus.FAIL)) &&
      isAdmin
    ) {
      const result = await confirmHydrogenMeasurementData(postData);

      // If both measurement & fuel data are CONFIRMED, try to issue GOs
      if (
        hydrogenFuelData?.get('status') === 'CONFIRMED' &&
        result?.status === 'CONFIRMED'
      ) {
        const productionUnitId = productionUnit.get('id');
        await issueHydrogenGos({
          productionUnitId,
          year: data.year,
          month: data.month,
        });
        await isGosIssued({
          productionUnitId,
          month: data.month,
          year: data.year,
        });
      }
    }

    onSubmitSuccess?.();

    return undefined;
  };

  render() {
    const {
      hasPrevious,
      hasDone,
      goPrevious,
      t,
      inEditForNotAdmin,
      isEditMode,
      isAdmin,
      loading,
      showRequestChangeModal,
      measurementData,
      surplus,
      locale,
      proportions,
      gosStatus,
      initialValues,
    } = this.props;

    const period = dayjs().subtract(1, 'month').format('MM.YYYY');
    const status = this.getStatus();

    const canUserSubmit =
      (isAdmin && status !== dataStatus.CONFIRMED) ||
      (!isAdmin && status === dataStatus.OPEN) ||
      (isAdmin &&
        status === dataStatus.CONFIRMED &&
        gosStatus === issuedGosStatus.FAIL);

    const showRequestChanges = !isAdmin && status !== dataStatus.OPEN;

    const totalProducedHydrogen = measurementData?.get('totalProducedHydrogen');
    const rfnboHydrogenGos = measurementData?.get('rfnboHydrogenGos');
    const otherRenewableHydrogenGos = measurementData?.get(
      'otherRenewableHydrogenGos'
    );
    const fossilHydrogenGos = measurementData?.get('fossilHydrogenGos');

    const isCalculatedMeasurementDataPresent =
      totalProducedHydrogen != null &&
      rfnboHydrogenGos != null &&
      otherRenewableHydrogenGos != null &&
      fossilHydrogenGos != null;

    const renderMeasurementData = (label, value) => (
      <div style={{ position: 'relative', width: locale === 'en' ? 180 : 130 }}>
        <b>{label}:</b>
        <div style={{ position: 'absolute', right: -30, top: 0 }}>
          <p>{value}</p>
        </div>
      </div>
    );

    return (
      <div>
        <Spin spinning={loading}>
          <Form
            initialValues={initialValues?.toJS()}
            initialValuesEqual={isEqual}
            validate={this.validate}
            onSubmit={this.onSubmit}
            render={({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit}>
                <FormStateToRedux form={FORM_MEASUREMENT_DATA} />
                <h2>{t('measurementData.title')}</h2>
                <p>{t('production_period') + ': ' + period}</p>
                <div className={`status_${status}`}>
                  {t('status')}: {t(status)}
                </div>

                <br />
                <h3>{t('measurementData.electricitySubtitle')}</h3>

                <Row gutter={32}>
                  <Col span={5} className="p-1">
                    <b>
                      {t(
                        `measurementData.${measurementDataFormFields.RFNBO_ELECTRICITY}`
                      )}
                    </b>
                  </Col>
                  <Col span={3}>
                    <Field
                      required
                      name={measurementDataFormFields.RFNBO_ELECTRICITY}
                      component={NumberField}
                      disabled={!canUserSubmit}
                      step={0.001}
                      precision={3}
                      min={0}
                      isFloat
                      style={{ width: '100%' }}
                      parse={(v) =>
                        v === '' || v == null ? undefined : Number(v)
                      }
                    />
                  </Col>
                  <Col span={2} className="p-1">
                    {proportions && (
                      <p>
                        {
                          proportions[
                            measurementDataFormFields.RFNBO_ELECTRICITY
                          ]
                        }
                        %
                      </p>
                    )}
                  </Col>
                </Row>

                <Row gutter={32}>
                  <Col span={5} className="p-1">
                    <b>
                      {t(
                        `measurementData.${measurementDataFormFields.OTHER_RENEWABLE_ELECTRICITY}`
                      )}
                    </b>
                  </Col>
                  <Col span={3}>
                    <Field
                      required
                      name={
                        measurementDataFormFields.OTHER_RENEWABLE_ELECTRICITY
                      }
                      component={NumberField}
                      disabled={!canUserSubmit}
                      step={0.001}
                      precision={3}
                      min={0}
                      isFloat
                      style={{ width: '100%' }}
                      parse={(v) =>
                        v === '' || v == null ? undefined : Number(v)
                      }
                    />
                  </Col>
                  <Col span={2} className="p-1">
                    {proportions && (
                      <p>
                        {
                          proportions[
                            measurementDataFormFields
                              .OTHER_RENEWABLE_ELECTRICITY
                          ]
                        }
                        %
                      </p>
                    )}
                  </Col>
                </Row>

                <Row gutter={32}>
                  <Col span={5} className="p-1">
                    <b>
                      {t(
                        `measurementData.${measurementDataFormFields.GRID_ELECTRICITY}`
                      )}
                    </b>
                  </Col>
                  <Col span={3}>
                    <Field
                      required
                      name={measurementDataFormFields.GRID_ELECTRICITY}
                      component={NumberField}
                      disabled={!canUserSubmit}
                      step={0.001}
                      precision={3}
                      min={0}
                      isFloat
                      style={{ width: '100%' }}
                      parse={(v) =>
                        v === '' || v == null ? undefined : Number(v)
                      }
                    />
                  </Col>
                  <Col span={2} className="p-1">
                    {proportions && (
                      <p>
                        {
                          proportions[
                            measurementDataFormFields.GRID_ELECTRICITY
                          ]
                        }
                        %
                      </p>
                    )}
                  </Col>
                </Row>

                <Row gutter={32}>
                  <Col span={5} className="p-1">
                    <b>
                      {t(
                        `measurementData.${measurementDataFormFields.FOSSIL_ELECTRICITY}`
                      )}
                    </b>
                  </Col>
                  <Col span={3}>
                    <Field
                      required
                      name={measurementDataFormFields.FOSSIL_ELECTRICITY}
                      component={NumberField}
                      disabled={!canUserSubmit}
                      step={0.001}
                      precision={3}
                      min={0}
                      isFloat
                      style={{ width: '100%' }}
                      parse={(v) =>
                        v === '' || v == null ? undefined : Number(v)
                      }
                    />
                  </Col>
                  <Col span={2} className="p-1">
                    {proportions && (
                      <p>
                        {
                          proportions[
                            measurementDataFormFields.FOSSIL_ELECTRICITY
                          ]
                        }
                        %
                      </p>
                    )}
                  </Col>
                </Row>

                <br />
                <Row gutter={32}>
                  <Col span={12} className="p-1">
                    <h3>{t('measurementData.hydrogenSubtitle')}</h3>
                  </Col>
                  <Col span={12} className="p-1">
                    {isCalculatedMeasurementDataPresent && (
                      <h3>{t('measurementData.goIssuanceSubtitle')}</h3>
                    )}
                  </Col>
                </Row>

                <Row gutter={32}>
                  <Col span={5} className="p-1">
                    <b>
                      {t(
                        `measurementData.${measurementDataFormFields.PRODUCED_HYDROGEN}`
                      )}
                    </b>
                  </Col>
                  <Col span={3}>
                    <Field
                      required
                      name={measurementDataFormFields.PRODUCED_HYDROGEN}
                      component={NumberField}
                      disabled={!canUserSubmit}
                      step={0.001}
                      precision={3}
                      min={0}
                      isFloat
                      style={{ width: '100%' }}
                      parse={(v) =>
                        v === '' || v == null ? undefined : Number(v)
                      }
                    />
                  </Col>
                  <Col span={4} />
                  {isCalculatedMeasurementDataPresent && (
                    <Col
                      span={5}
                      className="p-1"
                      style={{ textWrap: 'nowrap' }}
                    >
                      {renderMeasurementData(
                        t(
                          `measurementData.${measurementDataFormFields.RFNBO_HYDROGEN}`
                        ),
                        rfnboHydrogenGos
                      )}
                    </Col>
                  )}
                </Row>

                {isCalculatedMeasurementDataPresent && (
                  <>
                    <Row gutter={32}>
                      <Col span={5} className="p-1">
                        <b>
                          {t(
                            `measurementData.${measurementDataFormFields.SURPLUS}`
                          )}
                          :
                        </b>
                        <span style={{ marginLeft: 4 }}>
                          <Tooltip title={t('measurementData.surplusTooltip')}>
                            <QuestionCircleOutlined />
                          </Tooltip>
                        </span>
                      </Col>
                      <Col style={{ padding: '8px 28px' }} span={5}>
                        {surplus}
                      </Col>
                      <Col span={2} />
                      <Col
                        span={5}
                        className="p-1"
                        style={{ textWrap: 'nowrap' }}
                      >
                        {renderMeasurementData(
                          t(
                            `measurementData.${measurementDataFormFields.OTHER_RENEWABLE_HYDROGEN}`
                          ),
                          otherRenewableHydrogenGos
                        )}
                      </Col>
                    </Row>

                    <Row gutter={32}>
                      <Col span={5} className="p-1">
                        <b>
                          {t(
                            `measurementData.${measurementDataFormFields.TOTAL}`
                          )}
                          :
                        </b>
                      </Col>
                      <Col style={{ padding: '8px 28px' }} span={5}>
                        {totalProducedHydrogen}
                      </Col>
                      <Col span={2} />
                      <Col
                        span={5}
                        className="p-1"
                        style={{ textWrap: 'nowrap' }}
                      >
                        {renderMeasurementData(
                          t(
                            `measurementData.${measurementDataFormFields.FOSSIL_HYDROGEN}`
                          ),
                          fossilHydrogenGos
                        )}
                      </Col>
                    </Row>
                  </>
                )}

                <RegisterActions
                  showRequestChangeModal={showRequestChangeModal}
                  showRequestChangeButton={showRequestChanges}
                  hasPrevious={hasPrevious}
                  hasDone={hasDone}
                  isEditMode={isEditMode}
                  isAdmin={isAdmin}
                  goPrevious={goPrevious}
                  showSaveButton
                  inEditForNotAdmin={inEditForNotAdmin}
                  saveButtonDisabled={
                    status === dataStatus.CONFIRMED &&
                    gosStatus === issuedGosStatus.SUCCESS
                  }
                  t={t}
                  isMeasurementDataStep
                />

                <div className="component-register-actions">
                  <Button
                    size="large"
                    type="primary"
                    htmlType="submit"
                    disabled={!canUserSubmit || submitting}
                  >
                    {t('button_save')}
                  </Button>
                </div>

                {gosStatus && gosStatus !== issuedGosStatus.NA && (
                  <Alert
                    key={gosStatus}
                    style={{ marginTop: 20 }}
                    message={
                      gosStatus === issuedGosStatus.SUCCESS
                        ? t('goSuccess')
                        : t('goFailure')
                    }
                    type={
                      gosStatus === issuedGosStatus.SUCCESS
                        ? 'success'
                        : 'error'
                    }
                  />
                )}
              </form>
            )}
          />
        </Spin>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const existingData = getHydrogenMeasurementData(state);
  const surplus = getPreviousHydrogenMeasurementSurplus(state);
  const isGosIssued = getIsGosIssued(state);
  const issuedGosStatus = isGosIssued?.toJS().data?.status;
  const surplusNr =
    typeof surplus?.toJS().data === 'number' ? surplus?.toJS().data : 0.0;

  let proportions = null;
  if (existingData) {
    const rfnboAmount = existingData.get(
      measurementDataFormFields.RFNBO_ELECTRICITY
    );
    const otherAmount = existingData.get(
      measurementDataFormFields.OTHER_RENEWABLE_ELECTRICITY
    );
    const gridAmount = existingData.get(
      measurementDataFormFields.GRID_ELECTRICITY
    );
    const fossilAmount = existingData.get(
      measurementDataFormFields.FOSSIL_ELECTRICITY
    );

    if (
      existingData.get('status') !== dataStatus.OPEN &&
      rfnboAmount &&
      otherAmount &&
      gridAmount &&
      fossilAmount
    ) {
      const totalAmount = rfnboAmount + otherAmount + gridAmount + fossilAmount;
      proportions = {
        rfnboElectricity: round((rfnboAmount / totalAmount) * 100, 2),
        otherRenewableElectricity: round((otherAmount / totalAmount) * 100, 2),
        gridElectricity: round((gridAmount / totalAmount) * 100, 2),
        fossilElectricity: round((fossilAmount / totalAmount) * 100, 2),
      };
    }
  }

  return {
    loading: getLoadingMeasurementData(state),
    initialValues: existingData,
    measurementData: existingData,
    proportions,
    surplus: surplusNr,
    locale: getLocale(state),
    hydrogenFuelData: getHydrogenFuelData(state),
    gosStatus: issuedGosStatus,
  };
};

const mapDispatchToProps = {
  fetchHydrogenMeasurementDataForProductionPeriod,
  fetchPreviousHydrogenMeasurementSurplus,
  resetSurplus: resetPreviousHydrogenMeasurementSurplus,
  fetchHydrogenFuelDataForProductionPeriod,
  isGosIssued: fetchHydrogenGosIssued,
  resetGosIssued: resetHydrogenGosIssued,
  addHydrogenMeasurementData,
  confirmHydrogenMeasurementData,
  issueHydrogenGos,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AddEquipmentMeasurementData);
